import { Controller } from "@hotwired/stimulus";
import { WebAuthnSigner } from "@dfns/sdk-browser";
import toast from "../utils/toast";
import { getUserFriendlyErrorMessage } from "../utils/errors";
import { handleBackendResponse } from "../utils/backendResponseHandler";
import { debugLog, debugError } from "../utils";
import { rrFetch } from "../utils/rr_fetch"

export default class SignWithPasskeyController extends Controller {
  static targets = ["form", "confirmButton", "editButton", "spinner", "buttonText", "note"];
  static values = {
    challengeRequestUrl: { type: String, default: "/passkey/challenge/request" },
    canUseTransactionNotes: { type: Boolean, default: true },
    domain: String,
    company: String,
    idempotencyKey: String,
    csrfToken: String,
    payload: Object,
    provider: String,
    note: String,
    sendAttemptId: String,
  };

  connect() {
    this.tryAttachProcessTransfer()
  }

    tryAttachProcessTransfer() {
      const controller = this.application.getControllerForElementAndIdentifier(
        this.element.closest('[data-controller~="process-transfer"]'),
        "process-transfer"
      );

      if (controller) {
        this.processTransferController = controller;
      }
      debugLog(this.processTransferController)
    }

  async requestValidationChallenge(event) { // ----
    event.preventDefault();
    this.showLoading();
    try {
      // Step 1: Get challenge from backend (this will fail if no passkey exists)
      const challengeData = await this.getValidationChallenge();

      // Step 2: Sign challenge using WebAuthn (this is signing, not credential creation)
      const signedChallengeData = await this.createWebAuthnCredentialFromValidation(challengeData);

      // Step 3: Submit the signed challenge and execute transfer
      await this.executeTransaction(signedChallengeData);

    } catch (error) {
      debugError("Error in passkey validation:", error);

      if (error.message !== "backend-error") {
        if (error.name === "TypeError" || error.message.includes("fetch")) {
          toast("Unable to connect to server. Please check your connection.", "error");
        } else {
          toast(getUserFriendlyErrorMessage(error), "error");
        }
      }
      this.hideLoading();
    }
  }

  shouldIncludeNote() {
    if (!this.canUseTransactionNotesValue) {
      return false;
    }
    const source = this.payloadValue?.source;
    return source === "send" || source === "withdraw";
  }

  getCurrentNoteValue(fallback = "") {
    if (!this.canUseTransactionNotesValue) {
      return "";
    }

    let noteValue = fallback;
    if (this.hasNoteTarget) {
      noteValue = this.noteTarget.value || noteValue;
    } else if (this.processTransferController?.hasNoteTarget) {
      noteValue = this.processTransferController.noteTarget.value || noteValue;
    }

    return (noteValue || "").trim();
  }

  async getValidationChallenge() { // ----
    if (!this.hasPayloadValue || !this.payloadValue) {
      throw new Error("Payload is required but not provided");
    }

    const formData = new FormData();
    formData.append("payload", JSON.stringify(this.payloadValue));
    formData.append("idempotency_key", this.idempotencyKeyValue);
    formData.append("provider", this.providerValue);
    if (this.shouldIncludeNote()) {
      const noteValue = this.getCurrentNoteValue();
      if (noteValue) {
        formData.append("note", noteValue);
      }
    }
    formData.append("send_attempt_id", this.sendAttemptIdValue);

    const response = await rrFetch("/passkey/challenge/request", {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.csrfTokenValue,
      },
      body: formData,
    });
    return await handleBackendResponse(response, "Failed to get challenge");
  }

  async createWebAuthnCredentialFromValidation(challengeData) { // ----
    // For WebAuthn, relying party ID must match the current domain
    const currentDomain = globalThis.location.hostname;
    const relyingPartyId = this.domainValue || currentDomain;
    const relyingPartyName = this.companyValue || "CoinSub Wallet";

    let webauthn = new WebAuthnSigner({
      relyingParty: {
        id: relyingPartyId,
        name: relyingPartyName,
      },
    });

    const challengeIdentifier = challengeData.challengeIdentifier;
    const agreementId = challengeData.agreementId;
    const keyId = challengeData.keyId;
    const note = challengeData.note;
    // Get payload from challenge response
    const payload = challengeData.payload || {};

    const includeNote = this.shouldIncludeNote();
    const noteValue = includeNote ? this.getCurrentNoteValue(this.noteValue || note || "") : "";

    // Keep note in payload for send flow compatibility when notes are enabled.
    if (payload.source === "send") {
      if (includeNote && noteValue) {
        payload.note = noteValue;
      } else {
        delete payload.note;
      }
    }

    if (!challengeIdentifier) {
      throw new Error("Challenge identifier is missing from challenge data");
    }
    const assertion = await webauthn.sign(challengeData);

    const credentialAssertion = assertion.credentialAssertion;

    // Get all values from payload
    if (!payload) {
      throw new Error("Payload is required but not provided");
    }

    const signedChallengeData = {
      challengeIdentifier: challengeIdentifier,
      agreementId: agreementId,
      keyId: keyId,
      signature: {
        kind: "Fido2",
        credentialAssertion: {
          credId: credentialAssertion.credId || "",
          clientData: credentialAssertion.clientData || "",
          signature: credentialAssertion.signature || "",
          authenticatorData: credentialAssertion.authenticatorData || "",
          userHandle: credentialAssertion.userHandle || ""
        }
      },
      networkName: "Polygon",
      idempotencyKey: this.idempotencyKeyValue,
      payload: payload, // Send entire payload so backend has all data (walletAddress, houdiniTokenId, source, etc.)
      send_attempt_id: this.sendAttemptIdValue,
    };
    if (includeNote && noteValue) {
      signedChallengeData.note = noteValue;
    }

    return signedChallengeData;
  }

  async executeTransaction(signedChallengeData) { // ----
    signedChallengeData.idempotencyKey = this.idempotencyKeyValue;
    const response = await rrFetch('/passkey/challenge/submit', {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfTokenValue,
      },
      body: JSON.stringify(signedChallengeData),
    });

    return await handleBackendResponse(response, "Transaction failed");
  }

  showLoading() {
    // Disable confirm button
    this.confirmButtonTarget.disabled = true;

    // Disable edit link (anchor tag)
    if(this.hasEditButtonTarget) {
    this.editButtonTarget.classList.add('pointer-events-none', 'opacity-50');
    this.editButtonTarget.setAttribute('aria-disabled', 'true');
    }

    // Show spinner
    this.spinnerTarget.classList.remove('hidden');

    // Update text
    this.buttonTextTarget.textContent = "Processing...";
  }

  hideLoading() {
    // Enable confirm button
    this.confirmButtonTarget.disabled = false;

    // Enable edit link
    if(this.hasEditButtonTarget) {
    this.editButtonTarget.classList.remove('pointer-events-none', 'opacity-50');
    this.editButtonTarget.removeAttribute('aria-disabled');
    }

    // Hide spinner
    this.spinnerTarget.classList.add('hidden');

    // Restore text
    this.buttonTextTarget.textContent = "Confirm";
  }

}
